import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X, Check } from 'lucide-react';
import { cn } from '../utils/cn';

export interface FilterComboboxOption {
  value: string;
  label: string;
}

interface FilterComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: FilterComboboxOption[];
  allLabel?: string;
  searchPlaceholder?: string;
  className?: string;
  dark?: boolean;
}

export const FilterCombobox: React.FC<FilterComboboxProps> = ({
  value,
  onChange,
  options,
  allLabel = 'Todos',
  searchPlaceholder = 'Buscar...',
  className = '',
  dark = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = options.find(o => o.value === value)?.label;

  const sortedOptions = [...options].sort((a, b) =>
    a.label.localeCompare(b.label, 'pt-BR', { numeric: true, sensitivity: 'base' })
  );

  const filtered = search
    ? sortedOptions.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : sortedOptions;

  const updatePosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      minWidth: rect.width,
      zIndex: 9999,
    });
  };

  const handleOpen = () => {
    updatePosition();
    setOpen(prev => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const handleClose = (e: MouseEvent) => {
      if (buttonRef.current && buttonRef.current.contains(e.target as Node)) return;
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      setOpen(false);
      setSearch('');
    };
    const handleScroll = (e: Event) => {
      // Only close on scroll of the page/an ancestor — scrolling the dropdown's own
      // option list also fires a capture-phase 'scroll' on window (capture-phase
      // listeners see events from descendants too), which would otherwise close the
      // dropdown the instant you tried to scroll it.
      if (dropdownRef.current && e.target instanceof Node && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', handleClose);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClose);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  const handleSelect = (optValue: string) => {
    onChange(optValue);
    setOpen(false);
    setSearch('');
  };

  const dropdown = open && (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className={cn(
        'border rounded-lg shadow-lg py-1 w-max max-w-[240px]',
        dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      )}
    >
      {/* Search */}
      <div className={cn('px-2 py-1.5 border-b', dark ? 'border-gray-700' : 'border-gray-100')}>
        <div className={cn('flex items-center gap-1.5 px-2 py-1 rounded-md', dark ? 'bg-gray-800' : 'bg-gray-50')}>
          <Search className="h-3 w-3 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className={cn(
              'flex-1 bg-transparent text-xs placeholder-gray-500 outline-none min-w-0',
              dark ? 'text-gray-300' : 'text-gray-700'
            )}
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-500">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      <div className="max-h-52 overflow-y-auto custom-scrollbar">
        <button
          type="button"
          onClick={() => handleSelect('')}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors',
            dark
              ? !value ? 'font-medium text-gray-100 hover:bg-gray-800' : 'text-gray-400 hover:bg-gray-800'
              : !value ? 'font-medium text-gray-900 hover:bg-gray-50' : 'text-gray-500 hover:bg-gray-50'
          )}
        >
          <span className="flex-1">{allLabel}</span>
          {!value && <Check className={cn('h-3.5 w-3.5 flex-shrink-0', dark ? 'text-gray-400' : 'text-gray-600')} />}
        </button>

        {filtered.length === 0 ? (
          <p className="px-3 py-2 text-xs text-gray-400 italic">Nenhum resultado</p>
        ) : (
          filtered.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors',
                dark
                  ? value === option.value ? 'font-medium text-gray-100 hover:bg-gray-800' : 'text-gray-400 hover:bg-gray-800'
                  : value === option.value ? 'font-medium text-gray-900 hover:bg-gray-50' : 'text-gray-600 hover:bg-gray-50'
              )}
            >
              <span className="flex-1 truncate">{option.label}</span>
              {value === option.value && <Check className={cn('h-3.5 w-3.5 flex-shrink-0', dark ? 'text-gray-400' : 'text-gray-600')} />}
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className={cn('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        className={cn(
          'flex items-center gap-1.5 w-full px-3 py-1.5 border rounded-lg text-xs transition-colors focus:outline-none',
          dark
            ? value
              ? 'bg-gray-800 border-gray-500 text-gray-100 font-medium'
              : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-300'
            : value
              ? 'bg-white border-gray-900 text-gray-900 font-medium'
              : 'bg-white border-gray-300 text-gray-500 hover:border-gray-400'
        )}
      >
        <span className="flex-1 text-left truncate">
          {selectedLabel ?? allLabel}
        </span>
        {value ? (
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); handleSelect(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleSelect(''); } }}
            className={cn('flex-shrink-0 cursor-pointer', dark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700')}
          >
            <X className="h-3.5 w-3.5" />
          </span>
        ) : (
          <ChevronDown className={cn(
            'h-3.5 w-3.5 flex-shrink-0 transition-transform',
            dark ? 'text-gray-500' : 'text-gray-400',
            open && 'rotate-180'
          )} />
        )}
      </button>

      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
};

export default FilterCombobox;
